import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { demoQueryObjectives } from '../utils/demoData'
import {
  Target,
  Loader2,
  AlertTriangle
} from 'lucide-react'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts'

interface QueryObjective {
  objective_slug: string
  objective_name: string
  description: string
  query_count: number
  avg_confidence: number
  your_mentions: number
  percentage: string
  your_mention_rate: string
  example_queries?: string[]
}

interface QueryObjectivesPieChartProps {
  userBrandId: string
  demoMode?: boolean
}

const OBJECTIVE_COLORS: Record<string, string> = {
  discovery: '#8B5CF6',      // Purple
  comparison: '#3B82F6',     // Blue
  pricing: '#10B981',        // Green
  'problem-solving': '#F59E0B', // Orange
  research: '#EC4899',       // Pink
  'purchase-intent': '#EF4444', // Red
  support: '#6366F1'         // Indigo
}

export default function QueryObjectivesPieChart({ userBrandId, demoMode = false }: QueryObjectivesPieChartProps) {
  const [objectives, setObjectives] = useState<QueryObjective[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchObjectives()
  }, [userBrandId])

  const fetchObjectives = async () => {
    try {
      setLoading(true)
      setError(null)

      // Use demo data in demo mode without making API call
      if (demoMode) {
        setObjectives(demoQueryObjectives as any)
        setLoading(false)
        return
      }

      const response = await api.get(`/categories/${userBrandId}/objectives`)

      if (response.data.success) {
        setObjectives(response.data.objectives)
      }
    } catch (err: any) {
      console.error('Error fetching objectives:', err)
      setError(err.response?.data?.error || 'Failed to load objectives')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading objectives...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-2 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (objectives.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Query Data Yet
        </h3>
        <p className="text-gray-600">
          Add and classify queries to see objective breakdown
        </p>
      </div>
    )
  }

  // Prepare data for pie chart
  const chartData = objectives.map(obj => ({
    name: obj.objective_name,
    value: parseInt(obj.query_count as any),
    color: OBJECTIVE_COLORS[obj.objective_slug] || '#6B7280'
  }))

  return (
    <div className="min-w-0">
      <h3 className="text-center text-sm text-gray-600 mb-4">Query Objectives Share</h3>
      <ResponsiveContainer width="100%" height={300} minWidth={0}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={(props: any) => `${props.name} ${(props.percent * 100).toFixed(0)}%`}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}
